import { defineStore } from "pinia";
import { ref, computed } from "vue";
import {
    userApi,
    type User,
    type UserFilters,
    type CreateUserRequest,
    type UpdateUserRequest,
} from "@/api/user";

export const useUserStore = defineStore("user", () => {
    // State
    const users = ref<User[]>([]);
    const currentUser = ref<User | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    const page = ref(1);
    const perPage = ref(10);
    const search = ref("");
    const status = ref("");
    const role = ref("");
    const emailVerified = ref("");
    const meta = ref<{
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from?: number;
        to?: number;
    } | null>(null);

    // Getters
    const totalPages = computed(() => meta.value?.last_page || 0);
    const paginatedData = computed(() => users.value);
    const activeUsers = computed(() =>
        users.value.filter((user: User) => user.status),
    );
    const verifiedUsers = computed(() =>
        users.value.filter((user: User) => user.email_verified_at),
    );
    const inactiveUsers = computed(() =>
        users.value.filter((user: User) => !user.status),
    );
    const unverifiedUsers = computed(() =>
        users.value.filter((user: User) => !user.email_verified_at),
    );

    // Actions
    const fetchUsers = async (filters: UserFilters = {}) => {
        loading.value = true;
        error.value = null;

        try {
            const response = await userApi.listUsers({
                page: page.value,
                per_page: perPage.value,
                search: search.value || undefined,
                status: status.value as "active" | "inactive" | undefined,
                role: role.value || undefined,
                email_verified: emailVerified.value as
                    | "verified"
                    | "unverified"
                    | undefined,
                ...filters,
            });

            users.value = response.data?.items || response.data || [];
            meta.value = response.data?.meta || null;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to fetch users";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const fetchUser = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            currentUser.value = await userApi.getUser(id);
            return currentUser.value;
        } catch (err: any) {
            error.value = err.response?.data?.message || "Failed to fetch user";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const createUser = async (data: CreateUserRequest) => {
        loading.value = true;
        error.value = null;

        try {
            const newUser = await userApi.createUser(data);
            // Refresh the list to ensure consistency
            await fetchUsers();
            return newUser;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to create user";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateUser = async (id: string, data: UpdateUserRequest) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedUser = await userApi.updateUser(id, data);
            const index = users.value.findIndex((u: User) => u.id === id);
            if (index !== -1) {
                users.value[index] = updatedUser;
            }
            if (currentUser.value?.id === id) {
                currentUser.value = updatedUser;
            }
            return updatedUser;
        } catch (err: any) {
            // Don't set global error for validation errors (422) - let modal handle them
            if (err.response?.status !== 422) {
                error.value =
                    err.response?.data?.message || "Failed to update user";
            }
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const deleteUser = async (id: string) => {
        loading.value = true;
        error.value = null;

        try {
            await userApi.deleteUser(id);
            users.value = users.value.filter((u: User) => u.id !== id);
            if (currentUser.value?.id === id) {
                currentUser.value = null;
            }
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to delete user";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const assignRole = async (userId: string, roleId: number) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedUser = await userApi.assignRole(userId, roleId);
            const index = users.value.findIndex((u: User) => u.id === userId);
            if (index !== -1) {
                users.value[index] = updatedUser;
            }
            if (currentUser.value?.id === userId) {
                currentUser.value = updatedUser;
            }
            return updatedUser;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to assign role";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const removeRole = async (userId: string, roleId: number) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedUser = await userApi.removeRole(userId, roleId);
            const index = users.value.findIndex((u: User) => u.id === userId);
            if (index !== -1) {
                users.value[index] = updatedUser;
            }
            if (currentUser.value?.id === userId) {
                currentUser.value = updatedUser;
            }
            return updatedUser;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to remove role";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const toggleStatus = async (userId: string) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedUser = await userApi.toggleStatus(userId);
            const index = users.value.findIndex((u: User) => u.id === userId);
            if (index !== -1) {
                users.value[index] = updatedUser;
            }
            if (currentUser.value?.id === userId) {
                currentUser.value = updatedUser;
            }
            return updatedUser;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to toggle user status";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const verifyUser = async (userId: string) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedUser = await userApi.verifyUser(userId);
            const index = users.value.findIndex((u: User) => u.id === userId);
            if (index !== -1) {
                users.value[index] = updatedUser;
            }
            if (currentUser.value?.id === userId) {
                currentUser.value = updatedUser;
            }
            return updatedUser;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to verify user";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const unverifyUser = async (userId: string) => {
        loading.value = true;
        error.value = null;

        try {
            const updatedUser = await userApi.unverifyUser(userId);
            const index = users.value.findIndex((u: User) => u.id === userId);
            if (index !== -1) {
                users.value[index] = updatedUser;
            }
            if (currentUser.value?.id === userId) {
                currentUser.value = updatedUser;
            }
            return updatedUser;
        } catch (err: any) {
            error.value =
                err.response?.data?.message || "Failed to unverify user";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    // Profile specific methods
    const profile = ref<User | null>(null);
    const roleProfile = ref<any>(null);

    const fetchProfile = async () => {
        loading.value = true;
        error.value = null;

        try {
            console.log("DEBUG: Fetching profile from API");
            const response = await userApi.getProfile();
            profile.value = response.user;
            currentUser.value = response.user;
            roleProfile.value = response.profile;
            console.log("DEBUG: Profile loaded successfully:", profile.value);
            console.log("DEBUG: Role-specific profile data:", response.profile);
            return { user: response.user, profile: response.profile };
        } catch (err: any) {
            console.error("DEBUG: Error fetching profile:", err);
            error.value =
                err.response?.data?.message || "Failed to load profile data";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const updateUserProfile = async (data: UpdateUserRequest) => {
        loading.value = true;
        error.value = null;

        try {
            console.log("DEBUG: Updating profile with data:", data);
            const response = await userApi.updateProfile(data);
            profile.value = response.user;
            currentUser.value = response.user;
            roleProfile.value = response.profile;
            console.log("DEBUG: Profile updated successfully:", response.user);
            console.log(
                "DEBUG: Updated role-specific profile data:",
                response.profile,
            );
            return { user: response.user, profile: response.profile };
        } catch (err: any) {
            console.error("DEBUG: Error updating profile:", err);
            error.value =
                err.response?.data?.message || "Failed to update profile";
            throw err;
        } finally {
            loading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const setPage = (newPage: number) => {
        if (!meta.value) return;
        if (newPage < 1 || newPage > meta.value.last_page) return;

        page.value = newPage;
        fetchUsers();
    };

    const setPerPage = (newPerPage: number) => {
        perPage.value = newPerPage;
        page.value = 1;
        fetchUsers();
    };

    const resetFilters = () => {
        page.value = 1;
        perPage.value = 10;
        search.value = "";
        status.value = "";
        role.value = "";
        emailVerified.value = "";
        fetchUsers();
    };

    return {
        // State
        users,
        currentUser,
        profile,
        roleProfile,
        loading,
        error,
        page,
        perPage,
        search,
        status,
        role,
        emailVerified,
        meta,

        // Getters
        totalPages,
        paginatedData,
        activeUsers,
        verifiedUsers,
        inactiveUsers,
        unverifiedUsers,

        // Actions
        fetchUsers,
        fetchUser,
        createUser,
        updateUser,
        deleteUser,
        assignRole,
        removeRole,
        toggleStatus,
        verifyUser,
        unverifyUser,
        fetchProfile,
        updateUserProfile,
        clearError,
        setPage,
        setPerPage,
        resetFilters,
    };
});
